import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { Upload } from 'lucide-react';
import ModalCustom from '../../UI/modal';
import Button from '../../UI/button';
import { IdType } from '../../types/card';
import { useEditTripNameImage } from './use-edit-trip-name-image';
import ThInputText from '../../forms/th-input-text';
import imageUploadService from '../../../services/image-upload.service';
import '../photos/upload-trip-photos.scss';

type Props = {
  show: boolean;
  handleClose: () => void;
  id?: IdType | null;
  tripTitle?: string;
  tripImageUrl?: string;
  onSuccess?: () => void;
};

const EditTripNameImageModal = ({
  show,
  handleClose,
  id,
  tripTitle = '',
  tripImageUrl = '',
  onSuccess
}: Props) => {
  const tripId = id ? String(id) : null;

  const { tripData, validated, isSubmitting, handleInputChange, handleSubmit } =
    useEditTripNameImage(tripId, tripTitle, tripImageUrl, () => {
      handleClose();
      onSuccess?.();
    });

  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const isValidImageUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return (
        /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(urlObj.pathname) ||
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('data:image/')
      );
    } catch {
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setUrlInput(url);
    setSelectedFileName(''); // Clear file name when URL is pasted
    setSelectedFile(null); // Clear file when URL is pasted

    if (url && isValidImageUrl(url)) {
      setPreview(url);
      handleInputChange('imageUrl', url);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      alert(
        `File is too large (${(file.size / 1024 / 1024).toFixed(
          1
        )}MB). Maximum size is 10MB.`
      );
      return;
    }

    // Store the file object for later upload
    setSelectedFile(file);
    setUrlInput(file.name); // Display file name in URL input
    setSelectedFileName(file.name);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!file.type.startsWith('image/')) {
        alert('Please drop an image file');
        return;
      }

      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
      if (file.size > MAX_FILE_SIZE) {
        alert(
          `File is too large (${(file.size / 1024 / 1024).toFixed(
            1
          )}MB). Maximum size is 10MB.`
        );
        return;
      }

      // Store the file object for later upload
      setSelectedFile(file);
      setUrlInput(file.name); // Display file name in URL input
      setSelectedFileName(file.name);

      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setPreview(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validated) return;

    try {
      // If a file was selected, upload to S3 first
      if (selectedFile) {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const dataUrl = reader.result as string;
            const s3Key = await imageUploadService.uploadBase64Image(
              dataUrl,
              'appImages'
            );

            // Update imageUrl with S3 key before submitting
            handleInputChange('imageUrl', s3Key);

            // Small delay to ensure state is updated
            setTimeout(() => {
              handleSubmit(e);
            }, 100);
          } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
          }
        };
        reader.readAsDataURL(selectedFile);
      } else {
        // No file selected, just submit with existing or URL image
        handleSubmit(e);
      }
    } catch (error) {
      console.error('Error in form submit:', error);
      alert('Failed to update trip. Please try again.');
    }
  };

  // Sync preview and URL input when modal opens with trip data
  React.useEffect(() => {
    if (!show) {
      setPreview(null);
      setUrlInput('');
      setSelectedFileName('');
      setSelectedFile(null);
    } else if (tripData.imageUrl) {
      // When modal opens, check if it's a base64 data URL
      if (tripData.imageUrl.startsWith('data:image/')) {
        setUrlInput('[Uploaded Image]');
        setSelectedFileName('');
      } else {
        setUrlInput(tripData.imageUrl);
        setSelectedFileName('');
      }
      setPreview(null); // Clear preview so it uses tripData.imageUrl
      setSelectedFile(null);
    }
  }, [show, tripData.imageUrl]);

  return (
    <ModalCustom
      show={show}
      onHide={() => handleClose()}
      title='Edit Trip Name & Image'
    >
      <Form onSubmit={handleFormSubmit}>
        <ThInputText
          label='Trip Name'
          placeholder='Enter trip name'
          value={tripData.title}
          onChange={(value: string) => handleInputChange('title', value)}
          required
        />

        <Form.Group className='mb-3'>
          <Form.Label>Trip Image</Form.Label>
          <div
            className={`upload-trip-photos-dropzone ${
              dragActive ? 'active' : ''
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            style={{ cursor: 'pointer' }}
            onClick={() =>
              document.getElementById('trip-image-upload')?.click()
            }
          >
            {preview || tripData.imageUrl ? (
              <div style={{ position: 'relative' }}>
                <img
                  src={preview || tripData.imageUrl}
                  alt='Trip'
                  style={{
                    maxWidth: '100%',
                    maxHeight: '240px',
                    borderRadius: '8px',
                    objectFit: 'cover'
                  }}
                />
                <div
                  style={{
                    marginTop: '12px',
                    fontSize: '14px',
                    color: '#777'
                  }}
                >
                  Click or drag to change image
                </div>
              </div>
            ) : (
              <>
                <Upload className='upload-icon' />
                <p className='upload-text'>
                  Drag and drop an image here, or click to select
                </p>
              </>
            )}
            <input
              type='file'
              accept='image/*'
              onChange={handleFileChange}
              className='hidden'
              id='trip-image-upload'
              disabled={isSubmitting}
            />
          </div>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <small style={{ color: '#777', fontSize: '12px' }}>
              or paste URL
            </small>
            <Form.Control
              type='text'
              placeholder='https://example.com/image.jpg'
              value={urlInput}
              onChange={handleUrlChange}
              disabled={isSubmitting}
              style={{ marginTop: '8px' }}
            />
          </div>
          <Form.Text className='text-muted'>
            Maximum file size: 10MB. Supported formats: JPG, PNG, GIF.
          </Form.Text>
        </Form.Group>

        <div className='d-flex gap-2 justify-content-center'>
          <Button
            variant='secondary'
            className='mx-auto'
            size='md'
            width={153}
            type='button'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            className='mx-auto'
            size='md'
            width={153}
            type='submit'
            disabled={!validated || isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update'}
          </Button>
        </div>
      </Form>
    </ModalCustom>
  );
};

export default EditTripNameImageModal;
